import { ReactNode } from "react";

interface AppShellProps {
  menu: ReactNode;
  topBar: ReactNode;
  children: ReactNode;
}

/**
 * Three zones, never four. The hierarchy reads by background value alone — menu, then context,
 * then content, each one lighter than the last — and the zones are separated by a hairline,
 * never by a shadow. Backgrounds run to the edge: no white gutter between them.
 *
 * The menu runs the full height; the top bar spans everything to its right. Everything below
 * the bar belongs to the screen — including the context rail, when it wants one. The shell has
 * no slot to fill and nothing to coordinate, so the grid is right on the first frame.
 *
 * In the dark theme the hairline between menu and content becomes a hand-drawn stroke and two
 * grids fade in from the corners. Those are drawn in styles/index.css under `.app-*`; the light
 * theme draws none of them.
 */
export const AppShell = ({ menu, topBar, children }: AppShellProps) => (
  <div className="h-screen font-ui">
    <div className="app-frame grid grid-cols-shell-compact desk:grid-cols-shell h-full min-h-0 overflow-hidden bg-surface-bar">
      <div className="app-menu relative z-10 flex flex-col min-h-0 overflow-hidden bg-surface-menu border-r border-line-menu">
        {menu}
      </div>

      <svg className="app-divider" viewBox="0 0 18 1000" preserveAspectRatio="none" aria-hidden="true">
        <path d="M9 0 C 11 60, 6 110, 9 170 S 12 280, 8 340 S 5 450, 9 520 S 13 620, 9 690 S 5 800, 9 860 S 11 950, 9 1000" />
      </svg>

      <div className="flex flex-col min-w-0 min-h-0">
        {topBar}

        {/* Each screen owns its own layout and scrolling, so headers and filters can stay put. */}
        <main className="flex flex-1 min-w-0 min-h-0 overflow-hidden bg-surface-content">
          {children}
        </main>
      </div>
    </div>
  </div>
);
